/**
 * The hub's frame: the rail down the left, the bar across the top.
 *
 * WRAPS pages rather than replacing them. Everything already built — Connor,
 * the questionnaires, the Submission Portal — renders inside this untouched.
 * That is deliberate: a shell that required rewriting its contents would be a
 * redesign, and this is navigation.
 *
 * ⚠️ It is applied ONLY to hub routes. COMM-PASS keeps its own top bar and
 * looks exactly as it does today; an agent who only uses COMM-PASS sees no
 * difference whatsoever.
 *
 * Five tools fit on a grid of tiles. Fifteen do not — which is the whole
 * argument for a rail, and why this arrives now rather than later.
 */
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HUB_MODULES, type HubModule } from '@commpass/shared';
import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { openConnor } from './AppetiteChat';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * Only drawn when the caller holds this module.
   *
   * ⚠️ Not security — the route and the API enforce that. This is so an agency
   * that has no idea what Policy Upload is never has to wonder. Josh, on Iron
   * Peak: "regular agents alliance agents don't see it, because they'll just
   * get confused."
   */
  needs?: string;
  /** Drawn but not reachable — see the comment on NOT_BUILT below. */
  soon?: boolean;
  /** For items that aren't routes: open Connor, or leave for another app. */
  action?: () => void;
}

const I = (d: string): ReactNode => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d={d} />
  </svg>
);

/**
 * ⚠️ THE RAIL IS GENERATED FROM HUB_MODULES NOW, NOT LISTED BY HAND. Loss Runs
 * shipped with a page, an API and a granted module — and no rail entry, so the
 * only way in was typing the URL. A hand-maintained list of the same things the
 * registry already knows about is a list that will be short again.
 *
 * Order comes from the array in modules.ts. Two behaviours are derived rather
 * than declared, because both follow from what the module already is:
 *
 *   `assistant` — Appetite IS Connor. There is no separate page, and inventing
 *                 one would mean two front doors to the same answer.
 *   `external`  — the Inspector is a separate deployment, so it opens in its
 *                 own tab rather than pretending to live here.
 */
const fromModule = (m: HubModule): NavItem => ({
  to: m.surface === 'assistant' || m.external ? '' : m.href,
  label: m.name,
  icon: I(m.icon),
  needs: m.id,
  action:
    m.surface === 'assistant'
      ? openConnor
      : m.external
        ? () => window.open(m.href, '_blank', 'noopener,noreferrer')
        : undefined,
});

/** Not modules: the hub itself, and a directory that lives in another product. */
const HOME: NavItem[] = [
  { to: '/hub', label: 'Commercial Hub', icon: I('M3 11l9-8 9 8M5 10v10h14V10') },
  // No `needs`: it describes only what the agency actually holds, so it is
  // useful to everyone and misleading to no one.
  { to: '/hub/programs', label: 'Your programmes', icon: I('M4 5h16v4H4zM4 13h16v6H4z') },
];

const ELSEWHERE: NavItem[] = [
  // The Carrier Directory already exists in Alliance365 — linking it beats
  // rebuilding it, and members keep one source of truth for appointments.
  {
    to: '',
    label: 'Markets & Carriers',
    icon: I('M3 5h18v14H3zM3 10h18'),
    action: () => window.open('https://www.alliance365.biz/cldirectory', '_blank', 'noopener,noreferrer'),
  },
];

const MAIN: NavItem[] = [...HOME, ...HUB_MODULES.filter((m) => m.rail).map(fromModule), ...ELSEWHERE];

/**
 * Shown greyed rather than hidden.
 *
 * A member who can see what is coming stops asking whether it exists, and it
 * sets the expectation that the hub is a place things get added to. Hiding them
 * would make the rail look finished when it is not.
 *
 * ⚠️ ONLY THINGS WE ACTUALLY INTEND TO BUILD (Josh, 2026-08-30). "Reports" and
 * "Claims Resources" came off: we are not the carrier, so neither had a real
 * shape behind it. A greyed item is a promise, and promising something nobody
 * has decided to build is how a rail fills with doors that never open.
 */
const NOT_BUILT: NavItem[] = [
  { to: '#', label: 'Certificates', icon: I('M12 3v18M3 12h18'), soon: true },
];

const STAFF: NavItem[] = [
  { to: '/hub/member-submissions', label: 'Member submissions', icon: I('M4 5h16v14H4zM9 9h6M9 13h6') },
  { to: '/hub/admin', label: 'Manage access', icon: I('M12 4a4 4 0 100 8 4 4 0 000-8zM4 20c0-4 4-6 8-6s8 2 8 6') },
];

export function HubShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { email, signOut } = useAuth();

  /**
   * How many notifications are new.
   *
   * ⚠️ FETCHED ONCE PER SHELL MOUNT, NOT POLLED. A bell that polls every thirty
   * seconds is a request per user per thirty seconds for a number that changes a
   * handful of times a day — and it would still be stale between polls, so it
   * buys the appearance of liveness rather than liveness. It refreshes when you
   * move between hub pages, which is when you would look at it.
   */
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!email) return;
    let live = true;
    api
      .notifications()
      .then((r) => {
        if (live) setUnread(r.unread);
      })
      // A bell that cannot count is a bell with no number, never an error.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [email, pathname]);

  /**
   * What this person can open. The rail asks so it can hide what is not theirs;
   * every route and every endpoint checks for itself, so a stale answer here is
   * a cosmetic problem rather than a hole.
   */
  const [modules, setModules] = useState<string[] | null>(null);
  /**
   * Staff *of the hub* — the allow-list, not the email domain.
   *
   * ⚠️ useAuth().isStaff IS THE WRONG QUESTION HERE (2026-09-01). It asks "is
   * this an @agentsalliance.biz address", which is right for COMM-PASS and
   * wrong for the hub: it showed the AAS-staff section of this rail to every
   * employee, including the ones every hub API now refuses. The server decides
   * and the client repeats it, so there is no second opinion to drift.
   */
  const [hubStaff, setHubStaff] = useState(false);
  /** The one module this person is restricted to — the server's answer, not a guess. */
  const [pinned, setPinned] = useState<string | null>(null);
  useEffect(() => {
    api
      .hubMe()
      .then((r) => {
        setModules(r.modules);
        setHubStaff(Boolean(r.isStaff));
        setPinned(r.pinned ?? null);
      })
      .catch(() => {
        setModules([]);
        setHubStaff(false);
        setPinned(null);
      });
  }, [email]);
  const holds = (id?: string) => !id || (modules ?? []).includes(id);

  /*
    Someone restricted to a single module: the Policy-Upload-only Iron Peak
    pilot (Josh, 2026-08-31: "only this screen until we get the whole thing
    built and released"), and now the agent who uses the Account Inspector.

    The server decides this, not the client — modulesFor() returns exactly one
    module and every other hub API refuses, so this is presentation over a real
    gate rather than the gate itself.
  */
  /*
    ⚠️ THE SERVER'S `pinned`, NOT modules.length === 1. This was hard-coded to
    Iron Peak and inferred from the list; the Account Inspector needed the same
    treatment (Josh, 2026-09-04: "agent can access but can't access the hub"),
    and a second inference would have been a second thing to get wrong. The
    restriction is enforced in hubAllows regardless — this only decides what
    gets drawn.
  */
  const pilotOnly = modules !== null && pinned !== null;
  const pinnedHome = HUB_MODULES.find((m) => m.id === pinned)?.href ?? '/hub';

  /**
   * Send them to the one page they have, from wherever they landed — a bookmark
   * to /hub, or the redirect after a magic link. Waits for `modules`, because
   * null means "not known yet" and bouncing on an unknown would fire on every
   * cold load before the answer arrives.
   *
   * The destination comes from the module registry rather than a literal, so
   * pinning someone to a new module needs no change here.
   */
  useEffect(() => {
    if (pilotOnly && pathname !== pinnedHome) navigate(pinnedHome, { replace: true });
  }, [pilotOnly, pinnedHome, pathname, navigate]);

  // "/submission" must not light up for "/submissions-something", and "/hub"
  // must not stay lit on every page beneath it.
  const isOn = (to: string): boolean =>
    !to ? false : to === '/hub' ? pathname === '/hub' : pathname === to || pathname.startsWith(`${to}/`);

  const initials = (email ?? '?')
    .split('@')[0]!
    .split(/[._-]/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  const link = (n: NavItem) => (
    <button
      key={n.label}
      type="button"
      className={`hs-item${isOn(n.to) ? ' is-on' : ''}${n.soon ? ' is-soon' : ''}`}
      onClick={() => {
        if (n.soon) return;
        if (n.action) n.action();
        else navigate(n.to);
      }}
      aria-current={isOn(n.to) ? 'page' : undefined}
      aria-disabled={n.soon || undefined}
    >
      {n.icon}
      {n.label}
      {n.soon && <span className="hs-soon">Soon</span>}
    </button>
  );

  return (
    <div className="hs">
      <nav className="hs-rail" aria-label="Commercial Hub">
        <button type="button" className="hs-brand" onClick={() => navigate('/hub')}>
          <img src="/logo.png" alt="" />
          <span>
            <b>Commercial Hub</b>
            <em>Agents Alliance</em>
          </span>
        </button>

        {/*
          ⚠️ NOTHING UNTIL WE KNOW (Josh, 2026-08-31: "it shows for about 1
          second all the options in the left but then they all disappear").
          `modules` is null while /api/hub/me is in flight, and holds() answers
          true for every item that declares no `needs` — so the whole rail
          painted, then had six items taken off it a second later.

          Ugly for anyone; wrong for a pilot member, who was being shown a hub
          they are deliberately not supposed to know exists. An empty rail for
          200ms says nothing; a rail that flashes and retracts says "there is
          more here and you are not allowed it", which is the one message this
          restriction exists to avoid sending.

          Same mistake as the App.tsx hub-visibility race: deciding while
          loading, instead of waiting to be told.
        */}
        {modules !== null && (
          <>
            <div className="hs-group">
              {(pilotOnly
                ? MAIN.filter((n) => n.needs === pinned)
                : MAIN.filter((n) => holds(n.needs))
              ).map(link)}
            </div>

            {/* The greyed items exist to say the hub is a place things get added
                to — a promise that reads as a tease to somebody who has been
                given exactly one page on purpose. */}
            {!pilotOnly && (
              <>
                <div className="hs-label">Not built yet</div>
                <div className="hs-group">{NOT_BUILT.map(link)}</div>
              </>
            )}
          </>
        )}

        {hubStaff && (
          <>
            <div className="hs-label">AAS staff</div>
            <div className="hs-group">{STAFF.map(link)}</div>
          </>
        )}

        {/* CLHelp is the commercial lines desk; commpass@ is submission intake.
            An agent with a coverage question who writes to intake is queued
            behind submissions by people who cannot answer them. */}
        <div className="hs-help">
          <b>Need a hand?</b>
          <p>Your specialist team, not a ticket queue.</p>
          <a
            href={
              'mailto:CLHelp@agentsalliance.biz' +
              '?subject=' +
              encodeURIComponent('Commercial question from the Commercial Hub') +
              '&body=' +
              encodeURIComponent(
                `

—
Sent from the Commercial Hub${email ? ` by ${email}` : ''}`,
              )
            }
          >
            Contact AAS
          </a>
        </div>
      </nav>

      {/*
        ⚠️ ONLY THE FRONT DOOR LETS THE SKYLINE THROUGH. Every other hub page
        puts its own title straight onto this surface — .page-title is navy on
        light — so exposing a dark photograph behind them would leave dark text
        on a dark image on Supplementals, Programmes, Policy Upload and the
        submission builder alike. The front door is the one page whose content
        is entirely inside opaque cards, which is what makes the gutters safe to
        open up there and nowhere else.
      */}
      <div className={`hs-main${pathname === '/hub' ? ' is-front' : ''}`}>
        <div className="hs-top">
          {/* Deliberately a button, not an input: search across classes, carriers
              and forms is not built, and a text box that swallows what you type
              is worse than one that says so when you click it. */}
          {/* Hidden for the pilot: it navigates to Supplementals, which they
              cannot open, so it is a door to a redirect. Also held back while
              `modules` is unknown, or it flashes in and out like the rail did. */}
          {modules !== null && !pilotOnly && (
            <button type="button" className="hs-search" onClick={() => navigate('/hub/supplementals')}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" />
              </svg>
              Search classes, carriers, forms…
            </button>
          )}
          {/* COMM-PASS's own top bar is hidden on hub routes — the rail replaces
              it — so the two things it carried have to live here, or a hub page
              becomes a room with no door: no way back to the live portal, and
              no way to sign out. */}
          <div className="hs-who">
            {email && (
              <button
                type="button"
                className="hs-bell"
                onClick={() => navigate('/hub/notifications')}
                aria-label={unread ? `Notifications, ${unread} new` : 'Notifications'}
                title="Notifications"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16zM10 21a2 2 0 004 0" />
                </svg>
                {/* Capped, because a bell is a nudge and "47" is a wall. */}
                {unread > 0 && <span className="hs-bell-n">{unread > 9 ? '9+' : unread}</span>}
              </button>
            )}
            <button type="button" className="hs-exit" onClick={() => navigate('/')}>
              COMM-PASS
            </button>
            <div className="hs-avatar">{initials || '?'}</div>
            <div>
              <b>{email ?? 'Not signed in'}</b>
              <span>{hubStaff ? 'AAS staff' : 'Member'}</span>
            </div>
            {email && (
              <button type="button" className="hs-out" onClick={() => void signOut()} title="Sign out">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h9" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {/*
          ⚠️ THE PAGE WAITS TOO, NOT JUST THE RAIL. The redirect for a pilot
          member cannot fire until `modules` arrives, so /hub would render the
          whole front door — stat cards, tiles, everything — and only then
          bounce. Fixing the rail alone would have left the bigger leak in
          place, and made it look fixed.

          The cost is a brief blank on a cold load only: HubShell stays mounted
          across hub navigation and fetches once, so moving between hub pages
          never hits this.
        */}
        {modules === null ? (
          <div className="wrap">
            <p className="supp-note">Loading…</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
